import { MonetaryAmount, TextTooltip } from '@components'
import type { JournalJson, ReportRequestBody } from '@query'
import { formatDate } from '@utils'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronDown, FaChevronRight } from 'react-icons/fa'
import { JournalDocumentEntriesPanel } from './JournalDocumentEntriesPanel'
import { ReportDocumentPreview } from './ReportDocumentPreview'
import {
  Caret,
  ColumnHeaderRow,
  DateCell,
  JournalUnbalancedIcon,
  LineItemHeader,
  ReportRow,
  RowLabel,
  RowMeta,
  ValueCell,
  ValueHeader
} from './interactive.styled'
import { journalTableMinWidthPx } from './periodUtils'
import { JournalListEmptyState } from './ReportListEmptyState'
import { ReportCardLayout } from './ReportCardLayout'
import { ReportLeadingGutter } from './ReportLeadingGutter'
import type { ReportExpansion } from './interactiveReportsSession'
import { useOnTokenIncrement } from './useOnTokenIncrement'

type Props = {
  slug: string
  reportBody: ReportRequestBody
  data: JournalJson
  expansion: ReportExpansion
  onExpansionChange: (patch: Partial<ReportExpansion>) => void
  expandAllToken: number
  collapseAllToken: number
  currency?: string
  isRefreshing?: boolean
  isStale?: boolean
  hasTagFilter?: boolean
}

/** Narrow trailing slot for the unbalanced icon; keeps debit/credit columns aligned. */
const STATUS_ICON_WIDTH = '28px'

const entryCount = (count?: number, entries?: unknown[]) => count ?? entries?.length ?? 0

export const JournalView: React.FC<Props> = ({
  slug,
  reportBody,
  data,
  expansion,
  onExpansionChange,
  expandAllToken,
  collapseAllToken,
  currency,
  isRefreshing = false,
  isStale = false,
  hasTagFilter = false
}) => {
  const [t] = useTranslation()
  const expanded = expansion.items

  useOnTokenIncrement(expandAllToken, () => {
    const next: Record<string, boolean> = {}
    data.documents.forEach(doc => {
      next[doc.number] = true
    })
    onExpansionChange({ items: next })
  })

  useOnTokenIncrement(collapseAllToken, () => {
    onExpansionChange({ items: {} })
  })

  const documents = data.documents ?? []

  return (
    <ReportCardLayout
      title={t('constants.reportType.journal')}
      isRefreshing={isRefreshing}
      isStale={isStale}
      footNote={t('reports.interactive.footNote.journal')}
      tableMinWidth={journalTableMinWidthPx()}
    >
      <ColumnHeaderRow>
        <ReportLeadingGutter variant="date" />
        <LineItemHeader>{t('reports.interactive.dateVoucher')}</LineItemHeader>
        <ValueHeader $width="120px">{t('reports.interactive.debit')}</ValueHeader>
        <ValueHeader $width="120px">{t('reports.interactive.credit')}</ValueHeader>
        <ValueHeader $width={STATUS_ICON_WIDTH} />
      </ColumnHeaderRow>

      {documents.length === 0 ? (
        <JournalListEmptyState
          totals={data.totals}
          currency={currency}
          hasTagFilter={hasTagFilter}
          statusIconWidth={STATUS_ICON_WIDTH}
        />
      ) : (
        documents.map(doc => {
          const open = !!expanded[doc.number]
          const debit = doc.totals?.debet ?? 0
          const credit = doc.totals?.credit ?? 0
          const balanced = Math.abs(debit - credit) < 0.005
          const count = entryCount(doc.entries_count, doc.entries)

          return (
            <React.Fragment key={doc.number}>
              <ReportRow
                $clickable
                $bg={open ? '#f7fafd' : undefined}
                onClick={() =>
                  onExpansionChange({
                    items: { ...expanded, [doc.number]: !expanded[doc.number] }
                  })
                }
              >
                <Caret>{open ? <FaChevronDown /> : <FaChevronRight />}</Caret>
                <DateCell>{doc.date ? formatDate(doc.date) : ''}</DateCell>
                <RowLabel $link>
                  {doc.document_id ? (
                    <ReportDocumentPreview documentId={doc.document_id}>
                      {doc.number}
                    </ReportDocumentPreview>
                  ) : (
                    doc.number
                  )}
                  <RowMeta>{t('reports.interactive.entryCount', { count })}</RowMeta>
                </RowLabel>
                <ValueCell $width="120px">
                  <MonetaryAmount value={debit} currency={currency} />
                </ValueCell>
                <ValueCell $width="120px">
                  <MonetaryAmount value={credit} currency={currency} />
                </ValueCell>
                <ValueCell $width={STATUS_ICON_WIDTH}>
                  {!balanced ? (
                    <TextTooltip tooltip={t('reports.interactive.unbalanced')} placement="top">
                      <span>
                        <JournalUnbalancedIcon aria-hidden />
                      </span>
                    </TextTooltip>
                  ) : null}
                </ValueCell>
              </ReportRow>

              {open && (
                <JournalDocumentEntriesPanel
                  slug={slug}
                  reportBody={reportBody}
                  documentNumber={doc.number}
                  currency={currency}
                />
              )}
            </React.Fragment>
          )
        })
      )}
    </ReportCardLayout>
  )
}
